
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Icon, type IconName } from '@/components/ui/Icon'
import { cx } from '@/lib/ui'

export type MenuItem =
  | {
      kind?: 'item'
      key: string
      label: string
      icon?: IconName
      /** Keyboard equivalent, or a short fact, shown at the right. */
      hint?: string
      /** The whole label, for one too long for the menu's width. */
      title?: string
      danger?: boolean
      disabled?: boolean
      onSelect: () => void
    }
  | { kind: 'separator'; key: string }
  | { kind: 'heading'; key: string; label: string }

/**
 * The right-click menu over the canvas. Positioned at the pointer, inside
 * the canvas panel, and nudged back on-screen when it would spill past an
 * edge. Anything here is a shortcut: every action also lives in a panel or
 * the toolbar, because right-click is invisible until you try it and absent
 * on touch.
 */
export function CanvasMenu({
  x,
  y,
  items,
  onClose,
  wide = false,
}: {
  /** Pointer position relative to the canvas panel. */
  x: number
  y: number
  items: MenuItem[]
  onClose: () => void
  /** Room for equipment names, as in the Split list. */
  wide?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  // Keep the whole menu inside its panel: flip left/up when it would overflow.
  useLayoutEffect(() => {
    const menu = ref.current
    const panel = menu?.parentElement
    if (!menu || !panel) return
    const box = menu.getBoundingClientRect()
    const bounds = panel.getBoundingClientRect()
    const dx = x + box.width > bounds.width - 8 ? -box.width : 0
    const dy = y + box.height > bounds.height - 8 ? -box.height : 0
    setOffset({ x: dx, y: dy })
  }, [x, y, items])

  /**
   * The keyboard model a role="menu" promises.
   *
   * Focus never entered the menu, so arrow keys did nothing, Enter did nothing,
   * and the actions the Guide advertises — rename, set a length, duplicate —
   * were mouse-only. Focus moves to the first item on open and returns to
   * whatever was focused when it closes.
   */
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const firstItem = ref.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]:not([disabled])')
    firstItem?.focus()
    return () => {
      if (opener && document.body.contains(opener)) opener.focus()
    }
  }, [])

  const onMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
    if (!keys.includes(event.key)) return
    event.preventDefault()
    const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not([disabled])') ?? [])]
    if (items.length === 0) return
    const at = items.indexOf(document.activeElement as HTMLButtonElement)
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (at + 1 + items.length) % items.length
            : (at - 1 + items.length) % items.length
    items[next]?.focus()
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    const onDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onDown)
    window.addEventListener('wheel', onClose, { passive: true })
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('wheel', onClose)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      role="menu"
      className={cx('glass absolute z-30 rounded-2xl p-1.5 shadow-[var(--shadow-float)]', wide ? 'w-80' : 'w-56')}
      style={{ left: x + offset.x, top: y + offset.y }}
      onKeyDown={onMenuKeyDown}
      onContextMenu={(event) => event.preventDefault()}
    >
      {items.map((item) => {
        if (item.kind === 'separator') {
          return <div key={item.key} role="separator" className="my-1 border-t border-[var(--border)]" />
        }
        if (item.kind === 'heading') {
          return (
            <p key={item.key} className="px-2.5 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
              {item.label}
            </p>
          )
        }
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            title={item.title}
            onClick={() => {
              item.onSelect()
              onClose()
            }}
            className={cx(
              'flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed disabled:opacity-40',
              item.danger ? 'text-danger hover:bg-danger-soft' : 'text-foreground hover:bg-hover',
            )}
          >
            {item.icon ? (
              <Icon name={item.icon} className={cx('h-4 w-4 shrink-0', item.danger ? '' : 'text-muted')} />
            ) : (
              <span className="h-4 w-4 shrink-0" />
            )}
            <span className="flex-1 truncate">{item.label}</span>
            {item.hint && <span className="shrink-0 font-mono text-[10px] text-muted">{item.hint}</span>}
          </button>
        )
      })}
    </div>
  )
}
